import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import BrandLogo from '../components/BrandLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';

// Invited account (provisioned as portfolio_owner) clicks the emailed
// link. We confirm the invite, let them pick which additional roles to
// activate (they ALWAYS keep portfolio_owner), then activate the
// account and send them to passwordless email-code sign-in.
const ROLE_DESC = {
  facility_owner: 'Own a single facility (no portfolio-wide rollup)',
  facility_manager: 'Manage assigned facilities (no merchant config)',
  staff: 'Read + record payments on assigned facilities',
  viewer: 'Read-only, no changes',
};
const labelize = (r) =>
  r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const AcceptInvite = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();

  // loading | choose | done | error
  const [state, setState] = useState('loading');
  const [message, setMessage] = useState('');
  const [info, setInfo] = useState(null);
  const [extra, setExtra] = useState([]); // selected add-on roles
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState('error');
      setMessage('Invalid invite link.');
      return;
    }
    axios
      .get(`${API_URL}/api/auth/accept-invite/${token}`)
      .then((res) => {
        setInfo(res.data);
        setState('choose');
      })
      .catch((err) => {
        setState('error');
        setMessage(
          err.response?.data?.detail ||
            'This invite is invalid or has expired. Ask for a new one.',
        );
      });
  }, [token]);

  const toggle = (r) =>
    setExtra((s) =>
      s.includes(r) ? s.filter((x) => x !== r) : [...s, r],
    );

  const accept = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/accept-invite`, {
        token,
        roles: extra,
      });
      setState('done');
      const email = res.data?.email || info?.email || '';
      setTimeout(
        () =>
          navigate(
            `/login${email ? `?email=${encodeURIComponent(email)}` : ''}`,
          ),
        1800,
      );
    } catch (err) {
      setState('error');
      setMessage(
        err.response?.data?.detail || 'Could not activate the account.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f6f7fb] p-4">
      <div className="max-w-md w-full text-center">
        <BrandLogo className="mb-4" />
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-1">
          {state === 'done'
            ? 'Account activated'
            : state === 'choose'
            ? 'Welcome to mySMS360'
            : 'Setting up your account'}
        </h2>
        {state === 'choose' && info && (
          <p className="text-[13px] text-slate-500 mb-4">
            Hi {info.full_name || info.email} — your account is a{' '}
            <span className="font-semibold text-indigo-600">
              {labelize(info.primary_role || 'staff')}
            </span>
            {info.primary_role === 'portfolio_owner'
              ? '. You can also activate extra roles for day-to-day work (you keep full owner control either way).'
              : '. Activate your account to sign in.'}
          </p>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm text-left">
          {state === 'loading' && (
            <p className="text-[13px] text-slate-500 text-center">
              One moment…
            </p>
          )}

          {state === 'choose' && info && (
            <>
              <div className="rounded-lg bg-indigo-50/60 border border-indigo-100 px-3 py-2.5 mb-4">
                <p className="text-[13px] font-semibold text-slate-800">
                  {labelize(info.primary_role || 'staff')}
                </p>
                <p className="text-[11px] text-slate-500">
                  {info.primary_role === 'portfolio_owner'
                    ? 'Full control of your organization and every facility — always on.'
                    : (ROLE_DESC[info.primary_role] ||
                        'Your assigned role for this facility.')}
                </p>
              </div>

              {/* Only a provisioned portfolio_owner gets to add roles. */}
              {info.primary_role === 'portfolio_owner' &&
                (info.selectable_roles || []).length > 0 && (
                  <>
                    <p className="text-[12px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
                      Optional additional roles
                    </p>
                    <div className="space-y-2 mb-5">
                      {(info.selectable_roles || []).map((r) => (
                        <button
                          key={r}
                          type="button"
                          onClick={() => toggle(r)}
                          className={`w-full text-left rounded-lg border p-3 transition-all ${
                            extra.includes(r)
                              ? 'border-indigo-500 ring-2 ring-indigo-500/20 bg-indigo-50/50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <p className="text-[13px] font-semibold text-slate-900">
                            {labelize(r)}
                          </p>
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            {ROLE_DESC[r] || ''}
                          </p>
                        </button>
                      ))}
                    </div>
                  </>
                )}

              <button
                onClick={accept}
                disabled={submitting}
                className="w-full py-2.5 rounded-lg bg-indigo-600 text-white text-[13px] font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? 'Activating…' : 'Activate my account'}
              </button>
              {info.primary_role === 'portfolio_owner' && (
                <p className="text-[11px] text-slate-400 text-center mt-3">
                  You can change roles for yourself and your team anytime
                  in Settings → Users &amp; Roles.
                </p>
              )}
            </>
          )}

          {state === 'done' && (
            <p className="text-[13px] font-medium text-emerald-600 text-center">
              You're all set. Taking you to sign in with an email code…
            </p>
          )}

          {state === 'error' && (
            <div className="text-center">
              <p className="text-[13px] text-rose-600 mb-3">{message}</p>
              <a
                href="/login"
                className="text-[13px] font-medium text-indigo-600 hover:text-indigo-700"
              >
                Go to sign in →
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
